import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DEFAULT_ROLES } from "../../../../src/shared/schemas/WorldbuildingEntrySchema.js";
import Auth from "../../Auth";

const WORLDBUILDING_TEMPLATE_PREFIX = "worldbuilding:";
const createBlankFieldValue = () => ({ type: "doc", content: [{ type: "paragraph" }] });

const isValidTipTapDoc = (value) =>
  value && typeof value === "object" && value.type === "doc" && Array.isArray(value.content);

export const cloneWorldbuildingFields = (fields = []) =>
  fields.map((field) => ({
    ...field,
    value: isValidTipTapDoc(field.value) ? { ...field.value } : createBlankFieldValue(),
  }));
export const blankWorldbuildingFieldValues = (fields = []) => fields.map((field) => ({ ...field, value: createBlankFieldValue() }));

export const normalizeWorldbuildingEntityType = (entityType = "") => {
  if (typeof entityType !== "string") return "";
  return entityType.startsWith(WORLDBUILDING_TEMPLATE_PREFIX) ? entityType.slice(WORLDBUILDING_TEMPLATE_PREFIX.length) : entityType;
};

export const formatWorldbuildingTypeLabel = (entityType = "") =>
  normalizeWorldbuildingEntityType(entityType)
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const BUILTIN_TEMPLATES = DEFAULT_ROLES.map((role) => ({
  id: null,
  role: role.optionValue,
  label: role.name,
  defaultFields: cloneWorldbuildingFields(role.defaultFields),
  entityType: role.optionValue,
  source: "builtin",
}));

const BUILTIN_TEMPLATE_MAP = BUILTIN_TEMPLATES.reduce((acc, template) => {
  acc.set(template.role, template);
  return acc;
}, new Map());

export const buildWorldbuildingTemplates = (customTemplates = []) => {
  const templateMap = new Map();

  BUILTIN_TEMPLATES.forEach((template) => {
    templateMap.set(template.role, {
      ...template,
      defaultFields: cloneWorldbuildingFields(template.defaultFields),
    });
  });

  customTemplates.forEach((template) => {
    const role = normalizeWorldbuildingEntityType(template?.entityType);

    if (!role) return;

    const builtin = BUILTIN_TEMPLATE_MAP.get(role);

    templateMap.set(role, {
      id: template.id ?? null,
      role,
      label: builtin?.label || formatWorldbuildingTypeLabel(role),
      defaultFields: cloneWorldbuildingFields(template.defaultFields || builtin?.defaultFields || []),
      entityType: role,
      source: builtin ? "customOverride" : "custom",
    });
  });

  return Array.from(templateMap.values());
};

export const buildWorldbuildingRoleOptions = (templates = [], derivedRoles = []) => {
  const seen = new Set();
  const options = [];

  templates.forEach((template) => {
    if (!template?.role || seen.has(template.role)) return;
    seen.add(template.role);
    options.push({
      value: template.role,
      label: template.label,
      source: template.source === "custom" ? "custom" : "template",
      template,
    });
  });

  derivedRoles.forEach((role) => {
    const value = normalizeWorldbuildingEntityType(role?.rollName || role?.value || "");
    if (!value || seen.has(value)) return;
    seen.add(value);
    options.push({
      value,
      label: role?.label || formatWorldbuildingTypeLabel(value),
      source: "derived",
      template: role?.template,
    });
  });

  return options;
};

export const buildDerivedWorldbuildingRoles = (entries = [], templates = []) => {
  const existingRoleValues = new Set(templates.map((template) => template?.role).filter(Boolean));

  return Array.from(
    new Set(
      entries
        .map((entry) => normalizeWorldbuildingEntityType(entry?.role))
        .filter(Boolean)
        .filter((role) => !existingRoleValues.has(role))
    )
  ).map((role) => {
    const matchingEntry = entries.find((entry) => normalizeWorldbuildingEntityType(entry?.role) === role);
    return {
      rollName: role,
      label: formatWorldbuildingTypeLabel(role),
      template: matchingEntry?.customFields?.length
        ? {
            role,
            label: formatWorldbuildingTypeLabel(role),
            defaultFields: blankWorldbuildingFieldValues(cloneWorldbuildingFields(matchingEntry.customFields)),
          }
        : undefined,
    };
  });
};

export const useWorldbuildingTemplates = () => {
  const [customTemplates, setCustomTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const result = await Auth.fetchWithIdToken("/api/entity-default-templates/list");
      const templates = (result?.defaultTemplates || []).filter((template) => !template.entityType?.startsWith("character:"));
      setCustomTemplates(templates);
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: "worldbuilding_templates" } });
      setCustomTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  const templates = useMemo(() => buildWorldbuildingTemplates(customTemplates), [customTemplates]);
  const roleOptions = useMemo(() => buildWorldbuildingRoleOptions(templates), [templates]);

  const getTemplateForRole = useCallback(
    (role) => {
      const normalizedRole = normalizeWorldbuildingEntityType(role || templates[0]?.role);
      return templates.find((template) => template.role === normalizedRole) || templates[0];
    },
    [templates]
  );

  return {
    templates,
    customTemplates,
    roleOptions,
    loading,
    refreshTemplates: loadTemplates,
    getTemplateForRole,
  };
};
